import React, { useState } from 'react'
import { useRouter } from 'next/router'
import ApiClient from 'data/api/api_client'
import { ArrowLeftIcon } from 'components/icons'
import DestinationTag from 'components/DestinationTag'
import ArticleAuthor from 'components/ArticleAuthor'
import OurPropertiesSection from 'components/magazines/OurPropertiesSection'
import { slugify } from 'data/helpers/strings'
import Link from 'components/Link'
import CreateAnAccount from 'components/magazines/CreateAnAccount'
import ShareIcons from 'components/magazines/ShareIcons'
import { config } from 'data/config'
import { unescapeHtml } from 'data/helpers/html'
import PublicLayout from 'layouts/PublicLayout'
import ArticleHeader from 'components/magazines/article/header'
import { useScreenWidth } from 'data/hooks/useScreenWidth'
import classNames from 'classnames'
import PageHeader from 'components/sections/page/PageHeader'
import ArticleMiniAuthor from 'components/ArticleMiniAuthor'
import { RestArticle } from 'data/types/article'
import { RestProperty } from 'data/types/property'
import { GetServerSidePropsContext } from 'next'
import he from 'he'
import ArticleShareModal from 'components/magazines/ArticleShareModal'
import { NextSeoProps } from 'next-seo'

interface ArticlePropTypes {
  initialData: {
    article: RestArticle
    properties: RestProperty[]
  }
}

const ArticlePage = ({ initialData }: ArticlePropTypes) => {
  const router = useRouter()
  const { isMobile: isMobileDisplay } = useScreenWidth(1024)

  const [article] = useState(initialData?.article ?? {})

  const h1 = 'prose-h1:font-serif prose-h1:text-grey-900 prose-h1:tracking-title prose-h1:text-32 lg:prose-h1:text-48 prose-h1:tracking-title prose-h1:leading-title'
  const h2 = 'prose-h2:font-serif prose-h2:text-grey-900 prose-h2:tracking-title prose-h2:text-24 lg:prose-h2:text-36 prose-h2:tracking-title prose-h2:leading-[34px] lg:prose-h2:leading-[39px]'
  const h3 = 'prose-h3:font-serif prose-h3:text-grey-900 prose-h3:tracking-title prose-h3:text-20 lg:prose-h3:text-24 prose-h3:tracking-title prose-h3:leading-[24px] lg:prose-h3:leading-[30px]'

  const p = 'prose-p:text-grey-750 prose-p:leading-23 text-justify lg:text-15'
  const a = 'prose-a:text-primary-fresh prose-a:opacity-100 text-14 lg:text-15 leading-23'
  const figcaption = 'prose-figcaption:text-center prose-figcaption:pt-10'

  const img = 'prose-img:object-cover prose-img:h-full prose-img:rounded-6 prose-img:overflow-hidden'
  const figure = 'prose-figure:p-0 prose-figure:w-full prose-figure:my-[34px]'

  const [shareArticleModalOpen, setShareArticleModalOpen] = useState(false)

  const seo: NextSeoProps = {
    title: article?.title,
    description: article?.description,
    openGraph: {
      type: 'article',
      url: config.appURL + router.asPath,
      images: [
        {
          url: article?.thumbnail?.src,
          width: 922,
          height: 700,
          alt: article?.thumbnail?.alt_text,
        },
      ],
      article: {
        authors: [`${config.appURL}/magazine/authors/${article?.author?.slug}`],
        tags: (article?.categories ?? []).map(category => unescapeHtml(category?.name)),
      },
    },
  }

  return (
    <>
      <PublicLayout
        mobileNavBar={{
          visible: true,
          images: [{
            url: article?.thumbnail?.src,
            alt_text: article?.thumbnail?.alt_text ?? '',
          }],
          fade: true,
          back: true,
          share: {
            onShare: () => setShareArticleModalOpen(true),
          },
        }}
        seo={seo}
      >
        {isMobileDisplay
          ? <div className="space-y-16">
            <PageHeader
              label={article.tags?.[0]?.name}
              heading={he.decode(article.title)}
            />
            <ArticleMiniAuthor
              slug={article.author?.slug}
              timestamp={article.created_at}
              className={{ container: 'px-24' }}
            />
          </div>
          : <ArticleHeader article={article} />
        }

        <div
          className={classNames('flex max-w-[1800px] px-24 mx-auto justify-between gap-10', {
            'my-100': !isMobileDisplay,
          })}
        >
          <div className="hidden lg:block sticky top-100 self-start mt-[120px] max-w-[242px] w-full flex-shrink-0">
            <div className="flex items-center justify-center rounded-full cursor-pointer h-50 w-50 shadow-circle-button mb-30 hover:bg-grey-100" onClick={() => window.history.length > 1 ? router.back() : router.push('/magazine')}>
              <ArrowLeftIcon />
            </div>
            <h3 className="font-serif font-bold text-grey-900 text-20 lg:text-24 tracking-title leading-[20px] lg:leading-[23px] mb-30" dangerouslySetInnerHTML={{ __html: article?.title }} />
            <div className="mb-20">
              <DestinationTag destination={article?.destination} />
            </div>
            <p className="text-14 text-grey-500 tracking-1/2">{article?.created_at}</p>
            <p className="text-14 text-grey-500 tracking-1/2 leading-24">By <Link className="underline" href={'/magazine/authors/' + article?.author?.slug}>{article?.author?.name}</Link></p>
            <div className="mt-15">
              <ShareIcons article={article} />
            </div>
          </div>

          {
            article && (
              <div className="max-w-[950px] w-full mx-auto">
                <div className={classNames(
                  h1, h2, h3, p, a, figcaption, img, figure,
                  'prose max-w-[950px] w-full mb-50',
                )}>
                  <div dangerouslySetInnerHTML={{ __html: article?.content }} />
                </div>
                <ArticleAuthor slug={article?.author?.slug} />
              </div>
            )
          }

        </div>


        {
          (article?.destination && (initialData?.properties ?? []).length > 0)
            ? (
              <>
                <hr className="border-input-border" />
                <OurPropertiesSection properties={initialData.properties} />
              </>
            )
            : null
        }

        <CreateAnAccount />
      </PublicLayout>

      <ArticleShareModal
        isOpen={shareArticleModalOpen}
        onClose={() => setShareArticleModalOpen(false)}
        href={router.asPath}
      />
    </>
  )
}

export async function getServerSideProps ({
  req,
  params,
}: GetServerSidePropsContext) {
  const article = await ApiClient.withReq(req).magazine.getArticle(params?.article as string)

  let properties = []

  const destination = article?.destination

  if (destination) {
    const response = await ApiClient.withReq(req).properties.getPublishedProperties({
      country: slugify(destination.country),
      region: slugify(destination.country),
      subregion: slugify(destination.country),
    }, {
      includes: ['destinationCountry', 'destinationRegion', 'destinationSubRegion'],
      paginated: true,
      limit: 6,
    })

    properties = response.properties
  }

  return {
    props: {
      initialData: {
        article: article,
        properties: properties,
      },
    },
  }
}

export default ArticlePage
